import React, { useEffect, useState } from 'react';
import { ArrowLeftRight, Inbox, FilePlus, ScrollText, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { inv } from '../../lib/invApi';
import ScanButton from '../../components/inventory/ScanButton';

const Section = ({ title, icon: Icon, children, testId }) => (
  <section data-testid={testId} className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-3">
    <h3 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</h3>
    {children}
  </section>
);

const statusColor = (s) => ({
  pending: 'bg-amber-900/30 text-amber-400 border-amber-700',
  ordered: 'bg-cyan-900/30 text-cyan-400 border-cyan-700',
  fulfilled: 'bg-green-900/30 text-green-400 border-green-700',
  completed: 'bg-green-900/30 text-green-400 border-green-700',
  rejected: 'bg-red-900/30 text-red-400 border-red-700',
  cancelled: 'bg-[#262626] text-[#A3A3A3] border-[#262626]',
  in_transit: 'bg-blue-900/30 text-blue-400 border-blue-700',
  approved: 'bg-green-900/30 text-green-400 border-green-700',
}[s] || 'bg-[#262626] text-[#A3A3A3]');

const InventoryWorkflow = () => {
  const [transfers, setTransfers] = useState([]);
  const [mreqs, setMreqs] = useState([]);
  const [nireqs, setNireqs] = useState([]);
  const [movements, setMovements] = useState([]);

  const refresh = async () => {
    const [t, m, n, mv] = await Promise.all([
      inv.get('/transfers').then((r) => r.data),
      inv.get('/material-requests').then((r) => r.data),
      inv.get('/new-item-requests').then((r) => r.data),
      inv.get('/movements', { limit: 50 }).then((r) => r.data),
    ]);
    setTransfers(t); setMreqs(m); setNireqs(n); setMovements(mv);
  };
  useEffect(() => { refresh(); }, []);

  const completeTransfer = async (id) => { try { await inv.post(`/transfers/${id}/complete`); toast.success('Completed'); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const cancelTransfer = async (id) => { try { await inv.post(`/transfers/${id}/cancel`); toast.success('Cancelled'); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const setMreqStatus = async (id, status) => { try { await inv.patch(`/material-requests/${id}/status`, { status }); toast.success('Updated'); refresh(); } catch (e) { toast.error('Failed'); } };
  const approveNew = async (id) => { try { await inv.post(`/new-item-requests/${id}/approve`); toast.success('Approved — master item created'); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const rejectNew = async (id) => {
    const reason = window.prompt('Rejection reason?'); if (!reason) return;
    try { await inv.post(`/new-item-requests/${id}/reject`, { rejection_reason: reason }); toast.success('Rejected'); refresh(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div data-testid="inv-workflow-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Inventory · Workflow</h2>
          <p className="text-[#A3A3A3]">Transfers, material requests, new-item requests, and the live movement ledger.</p>
        </div>
        <ScanButton mode="scan" testId="transfers-scan-btn" onDone={refresh} />
      </div>

      <Section title="Transfers" icon={ArrowLeftRight} testId="inv-transfers-section">
        {transfers.length === 0 ? <p className="text-[#525252] text-sm">No transfers.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
            <thead className="border-b border-[#262626]"><tr><th className="text-left py-2 text-[#A3A3A3]">When</th><th className="text-left py-2 text-[#A3A3A3]">From → To</th><th className="text-left py-2 text-[#A3A3A3]">Items</th><th className="text-left py-2 text-[#A3A3A3]">By</th><th className="text-left py-2 text-[#A3A3A3]">Status</th><th className="text-right py-2 text-[#A3A3A3]">Actions</th></tr></thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id} data-testid={`inv-transfer-row-${t.id}`} className="border-b border-[#262626] last:border-0">
                  <td className="py-2 text-[#A3A3A3] text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                  <td className="py-2 text-white">{t.from_location_name} <span className="text-[#525252]">→</span> {t.to_location_name}</td>
                  <td className="py-2 text-[#A3A3A3]">{(t.items || []).length} item{(t.items || []).length === 1 ? '' : 's'}</td>
                  <td className="py-2 text-[#A3A3A3]">{t.requested_by_name}</td>
                  <td className="py-2"><Badge className={statusColor(t.status)}>{t.status.toUpperCase()}</Badge></td>
                  <td className="py-2 text-right">
                    {t.status === 'pending' && (
                      <div className="inline-flex gap-1">
                        <Button data-testid={`inv-transfer-complete-${t.id}`} onClick={() => completeTransfer(t.id)} size="sm" className="bg-green-700 hover:bg-green-600 h-8"><Check className="w-3.5 h-3.5 mr-1" /> Complete</Button>
                        <Button data-testid={`inv-transfer-cancel-${t.id}`} onClick={() => cancelTransfer(t.id)} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-8"><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Section>

      <Section title="Material Requests (re-stock)" icon={Inbox} testId="inv-mreq-section">
        {mreqs.length === 0 ? <p className="text-[#525252] text-sm">No material requests.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
            <thead className="border-b border-[#262626]"><tr><th className="text-left py-2 text-[#A3A3A3]">Item</th><th className="text-right py-2 text-[#A3A3A3]">Qty</th><th className="text-left py-2 text-[#A3A3A3]">Priority</th><th className="text-left py-2 text-[#A3A3A3]">Requester</th><th className="text-left py-2 text-[#A3A3A3]">Status</th><th className="text-right py-2 text-[#A3A3A3]">Actions</th></tr></thead>
            <tbody>
              {mreqs.map((r) => (
                <tr key={r.id} data-testid={`inv-mreq-row-${r.id}`} className="border-b border-[#262626] last:border-0">
                  <td className="py-2 text-white">{r.item_name}<span className="text-[#525252] text-xs ml-2">{r.item_sku}</span></td>
                  <td className="py-2 text-right text-white">{r.quantity}</td>
                  <td className="py-2 text-[#A3A3A3] uppercase text-xs">{r.priority}</td>
                  <td className="py-2 text-[#A3A3A3]">{r.requested_by_name}</td>
                  <td className="py-2"><Badge className={statusColor(r.status)}>{r.status.toUpperCase()}</Badge></td>
                  <td className="py-2 text-right">
                    {r.status === 'pending' && (
                      <div className="inline-flex gap-1">
                        <Button data-testid={`inv-mreq-order-${r.id}`} onClick={() => setMreqStatus(r.id, 'ordered')} size="sm" variant="outline" className="border-cyan-700/40 text-cyan-400 hover:bg-cyan-900/20 h-8">Ordered</Button>
                        <Button data-testid={`inv-mreq-fulfill-${r.id}`} onClick={() => setMreqStatus(r.id, 'fulfilled')} size="sm" className="bg-green-700 hover:bg-green-600 h-8">Fulfilled</Button>
                        <Button data-testid={`inv-mreq-reject-${r.id}`} onClick={() => setMreqStatus(r.id, 'rejected')} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-8">Reject</Button>
                      </div>
                    )}
                    {r.status === 'ordered' && (
                      <Button data-testid={`inv-mreq-fulfill2-${r.id}`} onClick={() => setMreqStatus(r.id, 'fulfilled')} size="sm" className="bg-green-700 hover:bg-green-600 h-8">Mark fulfilled</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Section>

      <Section title="New Item Requests" icon={FilePlus} testId="inv-nireq-section">
        {nireqs.length === 0 ? <p className="text-[#525252] text-sm">No new item requests.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
            <thead className="border-b border-[#262626]"><tr><th className="text-left py-2 text-[#A3A3A3]">Item</th><th className="text-left py-2 text-[#A3A3A3]">Vendor</th><th className="text-right py-2 text-[#A3A3A3]">Est $</th><th className="text-left py-2 text-[#A3A3A3]">Requester</th><th className="text-left py-2 text-[#A3A3A3]">Status</th><th className="text-right py-2 text-[#A3A3A3]">Actions</th></tr></thead>
            <tbody>
              {nireqs.map((r) => (
                <tr key={r.id} data-testid={`inv-nireq-row-${r.id}`} className="border-b border-[#262626] last:border-0">
                  <td className="py-2 text-white"><div>{r.name}</div><div className="text-[#525252] text-xs">{r.reason}</div></td>
                  <td className="py-2 text-[#A3A3A3]">{r.vendor || '—'}{r.vendor_sku && <span className="text-xs ml-1">({r.vendor_sku})</span>}</td>
                  <td className="py-2 text-right text-white">${parseFloat(r.estimated_cost || 0).toFixed(2)}</td>
                  <td className="py-2 text-[#A3A3A3]">{r.requested_by_name}</td>
                  <td className="py-2"><Badge className={statusColor(r.status)}>{r.status.toUpperCase()}</Badge></td>
                  <td className="py-2 text-right">
                    {r.status === 'pending' && (
                      <div className="inline-flex gap-1">
                        <Button data-testid={`inv-nireq-approve-${r.id}`} onClick={() => approveNew(r.id)} size="sm" className="bg-green-700 hover:bg-green-600 h-8"><Check className="w-3.5 h-3.5 mr-1" /> Approve & create item</Button>
                        <Button data-testid={`inv-nireq-reject-${r.id}`} onClick={() => rejectNew(r.id)} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-8">Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Section>

      <Section title="Movement Ledger (last 50)" icon={ScrollText} testId="inv-movements-section">
        <div className="overflow-x-auto"><table className="w-full text-xs min-w-[820px]">
          <thead className="border-b border-[#262626]"><tr>
            <th className="text-left py-2 text-[#A3A3A3]">When</th>
            <th className="text-left py-2 text-[#A3A3A3]">Item</th>
            <th className="text-left py-2 text-[#A3A3A3]">From → To</th>
            <th className="text-right py-2 text-[#A3A3A3]">Qty</th>
            <th className="text-left py-2 text-[#A3A3A3]">Reason</th>
            <th className="text-left py-2 text-[#A3A3A3]">By</th>
          </tr></thead>
          <tbody>
            {movements.map((m) => (
              <tr key={m.id} className="border-b border-[#262626] last:border-0">
                <td className="py-2 text-[#A3A3A3]">{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</td>
                <td className="py-2 text-white">{m.item_name}<span className="text-[#525252] ml-1">{m.item_sku && `· ${m.item_sku}`}</span></td>
                <td className="py-2 text-[#A3A3A3]">{m.from_location_name || '—'} <span className="text-[#525252]">→</span> {m.to_location_name || '—'}</td>
                <td className="py-2 text-right text-white">{m.quantity}</td>
                <td className="py-2"><Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30">{m.reason_code}</Badge></td>
                <td className="py-2 text-[#A3A3A3]">{m.user_name}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Section>
    </div>
  );
};

export default InventoryWorkflow;
